import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { BridgeStepTrace, TraceError } from "../../lib/traceApi";
import { fetchTraceBlob } from "../../lib/traceApi";
import {
  bridgeStepAnchorId,
  TRACE_ANCHOR_FOCUS_EVENT,
} from "../../lib/traceAnchors";
import { diffLines } from "../../lib/diff";
import { JsonViewer } from "./JsonViewer";

type Tab = "summary" | "subtree";

// SubTreeBlob shape per orchestrator/internal/models/run_trace.go +
// Round-1 synthetic-bridge writers. The bridge writes a JSON object
// with "before" + "after" keys; we render each side pretty-printed and
// diff them line-by-line. Fields beyond before/after (eg metadata) are
// tolerated by `extra`.
//
// BACKEND ASSUMPTION: Round-1 synthetic bridges (pre_run / post_run /
// rollback) are expected to populate `before_after_subtree_ref` only
// when there's a meaningful state delta. The empty-string default means
// "no subtree captured" → we hide the tab.
interface SubTreeBlob {
  before?: unknown;
  after?: unknown;
}

export function BridgeStepRow({
  step,
  parentStageErrors,
}: {
  step: BridgeStepTrace;
  // Errors from RunTrace.Errors bound to this bridge's parent stage
  // attempt. Surfaced in the expand panel so an operator sees the
  // higher-scope failure context even when this particular bridge step
  // succeeded — bridges sit between the DAG and the runner's post-DAG
  // validators, so the typical CREATE failure is: bridges OK + post-DAG
  // validator rejected → the answer the operator needs lives at
  // stage scope, not bridge-step scope.
  parentStageErrors?: TraceError[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<Tab>("summary");
  const anchorId = bridgeStepAnchorId(step);
  const ref = useRef<HTMLDivElement | null>(null);
  const hasSubtreeRef = step.before_after_subtree_ref !== "";

  useEffect(() => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    if (hash === anchorId) {
      setExpanded(true);
      requestAnimationFrame(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    function onFocus(e: Event) {
      const id = (e as CustomEvent<string>).detail;
      if (id === anchorId) {
        setExpanded(true);
      }
    }
    window.addEventListener(TRACE_ANCHOR_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TRACE_ANCHOR_FOCUS_EVENT, onFocus);
  }, [anchorId]);

  // Badge: FAIL when step.error is set, OK otherwise. No in-flight /
  // INCOMPLETE state for bridge steps: the backend records duration_ms=0
  // for completed steps (they run sub-second), so "duration_ms===0" is NOT
  // a reliable in-flight signal — using it mislabeled every completed
  // bridge step as never-completed. Bridge steps are fast and aren't
  // skeleton-pre-allocated; a spinner that flashes for <100ms added no
  // value. Sub-calls and stages keep their spinner/INCOMPLETE because
  // their outcome==="" IS a reliable not-yet-sealed signal.
  const badge = step.error ? { label: "FAIL", cls: "text-rose-300" } : { label: "OK", cls: "text-emerald-300" };

  return (
    <div id={anchorId} ref={ref} className="border-l border-gray-800 pl-2 text-sm scroll-mt-20">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 w-full text-left hover:bg-gray-800 px-2 py-1"
      >
        <span className="w-6 text-gray-500">{expanded ? "[-]" : "[+]"}</span>
        <span className={`w-16 text-xs font-mono ${badge.cls}`}>{badge.label}</span>
        <span className="font-mono">{step.step_id}</span>
        <span className="text-gray-400 text-xs ml-auto">{step.duration_ms}ms</span>
      </button>
      {expanded && (
        <div className="ml-4 mt-1 mb-2 border border-gray-700 rounded bg-gray-950 p-2 text-xs">
          {hasSubtreeRef && (
            <div className="flex gap-1 mb-2 border-b border-gray-700">
              <button
                onClick={() => setTab("summary")}
                className={`px-2 py-1 text-xs border-b-2 ${tab === "summary" ? "border-blue-400" : "border-transparent"}`}
              >
                summary
              </button>
              <button
                onClick={() => setTab("subtree")}
                className={`px-2 py-1 text-xs border-b-2 ${tab === "subtree" ? "border-blue-400" : "border-transparent"}`}
              >
                Sub-tree before/after
              </button>
            </div>
          )}
          {tab === "summary" || !hasSubtreeRef ? (
            <BridgeSummary step={step} parentStageErrors={parentStageErrors} />
          ) : (
            <BridgeSubTreePane refPath={step.before_after_subtree_ref} />
          )}
        </div>
      )}
    </div>
  );
}

function BridgeSummary({
  step,
  parentStageErrors,
}: {
  step: BridgeStepTrace;
  parentStageErrors?: TraceError[];
}) {
  const stageErrors = parentStageErrors ?? [];
  const stepPassed = !step.error;
  return (
    <div className="space-y-2">
      <div className="space-y-2">
        <div>
          <span className="text-gray-500">Description: </span>
          {step.description}
        </div>
        <div>
          <div className="text-gray-500 text-[10px] uppercase mb-1">Input summary</div>
          <div className="bg-black p-1 rounded">
            <JsonViewer value={step.input_summary ?? {}} defaultExpandDepth={1} showSearch={false} />
          </div>
        </div>
        <div>
          <div className="text-gray-500 text-[10px] uppercase mb-1">Output summary</div>
          <div className="bg-black p-1 rounded">
            <JsonViewer value={step.output_summary ?? {}} defaultExpandDepth={1} showSearch={false} />
          </div>
        </div>
        <div>
          <span className="text-gray-500">Mutations: </span>
          {(step.mutations ?? []).length > 0 ? (step.mutations ?? []).join(", ") : "—"}
        </div>
        {step.before_after_subtree_ref && (
          <div className="text-gray-400 text-[10px]">
            [Sub-tree before/after available — see "Sub-tree" tab above]
          </div>
        )}
      </div>

      {/* This step's own outcome — explicit success/failure callout. */}
      {stepPassed ? (
        <div className="bg-emerald-950/40 border border-emerald-700/40 p-2">
          <div className="text-emerald-300">✓ Bridge step completed without error</div>
        </div>
      ) : (
        <div className="bg-rose-950/40 border border-rose-700/40 p-2">
          <div className="text-rose-300 font-bold">Bridge step failed</div>
          <div className="mt-1 text-rose-100">{step.error}</div>
        </div>
      )}

      {/* Parent-stage errors — the bridge step passed but the wider stage
          attempt failed for a runner-level reason. Same bridging logic
          as ExpandPanel.ValidationTab: surface where the answer actually
          lives so the user doesn't conclude "I don't see what failed." */}
      {stepPassed && stageErrors.length > 0 && (
        <div className="bg-amber-950/40 border-l-2 border-amber-600 p-2">
          <div className="text-amber-300 text-[10px] uppercase mb-1">
            Parent stage failed at a higher scope ({stageErrors.length}{" "}
            error{stageErrors.length === 1 ? "" : "s"})
          </div>
          <div className="text-amber-100/90 mb-2">
            This bridge step ran to completion. The parent stage's attempt
            failed at the runner level — post-DAG validators, ApplyCreateDelta,
            supersession/coverage_claim applies, etc. Bridge steps only
            run inside the DAG layer scope; wider failures surface here:
          </div>
          <ul className="space-y-1">
            {stageErrors.map((e, i) => (
              <li key={i} className="font-mono">
                <span className="text-amber-400">[{e.source}/{e.code}]</span>{" "}
                <span className="text-amber-100">{e.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BridgeSubTreePane({ refPath }: { refPath: string }) {
  const { uid = "", pid = "", rid = "" } = useParams();
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ready"; before: string; after: string }
    | { status: "error"; message: string }
  >({ status: "idle" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetchTraceBlob(uid, pid, rid, refPath)
      .then((raw) => {
        if (cancelled) return;
        const parsed = parseSubTreeBlob(raw);
        setState({
          status: "ready",
          before: parsed.before,
          after: parsed.after,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ status: "error", message: String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [uid, pid, rid, refPath]);

  if (state.status === "loading" || state.status === "idle") {
    return <div className="text-gray-400">Loading sub-tree blob…</div>;
  }
  if (state.status === "error") {
    return <div className="text-rose-300">Failed to load: {state.message}</div>;
  }

  const segments = diffLines(state.before, state.after);
  const leftLines = segments.filter((s) => s.kind !== "add");
  const rightLines = segments.filter((s) => s.kind !== "del");

  return (
    <div className="grid grid-cols-2 gap-2 font-mono whitespace-pre-wrap">
      <div>
        <div className="text-gray-400 mb-1">Before</div>
        <div className="bg-gray-900 p-1 max-h-80 overflow-auto">
          {leftLines.map((s, i) => (
            <div
              key={i}
              className={s.kind === "del" ? "bg-rose-900/50 text-rose-200" : "text-ink-200"}
            >
              {s.text || " "}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-gray-400 mb-1">After</div>
        <div className="bg-gray-900 p-1 max-h-80 overflow-auto">
          {rightLines.map((s, i) => (
            <div
              key={i}
              className={s.kind === "add" ? "bg-emerald-900/50 text-emerald-200" : "text-ink-200"}
            >
              {s.text || " "}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function parseSubTreeBlob(raw: string): { before: string; after: string } {
  try {
    const obj = JSON.parse(raw) as SubTreeBlob;
    return {
      before: JSON.stringify(obj.before ?? null, null, 2),
      after: JSON.stringify(obj.after ?? null, null, 2),
    };
  } catch {
    // Malformed blob: surface raw text on both sides so the admin can
    // at least see what was returned (the diff degenerates to one big
    // "eq" block, which is fine).
    return { before: raw, after: raw };
  }
}
